import React, { useState, useEffect } from 'react';
import { RefreshCw, Trophy, TrendingUp, Calendar, Info, X, ChevronUp, ChevronDown, Users, Target, PieChart as PieChartIcon } from 'lucide-react';
import { 
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, Cell,
  FunnelChart, Funnel, LabelList, PieChart, Pie, Legend
} from 'recharts';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { rewardService, studentService } from '../../services';
import LoadMoreButton from '../ui/LoadMoreButton';
import { formatHours } from '../../utils/helpers';
import { useToast } from '../../hooks/useToast';

const ACTION_BADGES = {
  'EARN_REFERRAL': { color: 'bg-green-100 text-green-800', label: '获得推荐赠送' },
  'EARN_VOLUME_BONUS': { color: 'bg-purple-100 text-purple-800', label: '获得达标赠送' },
  'TRANSFER': { color: 'bg-gray-100 text-gray-500', label: '历史结转' },
  'CONSUME': { color: 'bg-gray-100 text-gray-800', label: '上课核销' },
  'VOID_CONSUME': { color: 'bg-orange-100 text-orange-800', label: '上课作废' },
};

const STATUS_BADGES = {
  'PENDING': { color: 'bg-amber-100 text-amber-800', label: '在途' },
  'COMPLETED': { color: 'bg-emerald-100 text-emerald-800', label: '已发放' },
  'CANCELLED': { color: 'bg-rose-100 text-rose-800', label: '已取消' },
};

const PER_PAGE = 20;

const RewardManagementView = ({ searchTrigger = 0 }) => {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [stats, setStats] = useState({ total_bonus: 0, top_referrers: [] });
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [summaryLimit, setSummaryLimit] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activeStudents, setActiveStudents] = useState([]);
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [expandedLedgerIds, setExpandedLedgerIds] = useState(new Set());
  
  const [studentLedgerOpen, setStudentLedgerOpen] = useState(false);
  const [studentLedgerItems, setStudentLedgerItems] = useState([]);
  const [selectedStudentForLedger, setSelectedStudentForLedger] = useState(null);
  const [loadingStudentLedger, setLoadingStudentLedger] = useState(false);
  
  const [students, setStudents] = useState([]);
  const [filterStudentId, setFilterStudentId] = useState("");
  const [activeSubTab, setActiveSubTab] = useState('overview'); // 'overview' | 'details'
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Load student list once
  useEffect(() => {
    studentService.getList({ page: 1, per_page: 1000, sort_by: 'name', sort_order: 'asc' }).then(res => setStudents(res.items || [])).catch(() => {});
  }, []);
  
  const toast = useToast();

  const fetchGlobalStats = async () => {
    const [statsRes, activeRes] = await Promise.all([
      rewardService.getStats(year === 'all' ? null : year).catch(() => ({ total_bonus: 0, top_referrers: [] })),
      rewardService.getActiveStudents(year === 'all' ? null : year).catch(() => [])
    ]);
    setStats(statsRes);
    setActiveStudents(Array.isArray(activeRes) ? activeRes : []);
  };

  const fetchSummaries = async (isLoadMore = false) => {
    const nextLimit = isLoadMore ? summaryLimit + 20 : 20;
    if (!isLoadMore) setSummaryLimit(20);
    else setSummaryLimit(nextLimit);

    const res = await rewardService.getSummary(year === 'all' ? null : year, 1, nextLimit, filterStudentId).catch(() => ({ items: [], total: 0 }));
    setSummaries(res.items || []);
    setTotalCount(res.total || 0);
  };

  const loadData = async (isRefresh = false, isLoadMore = false) => {
    try {
      if (isLoadMore) setLoadingMore(true);
      else if (isRefresh) setRefreshing(true);
      else setLoading(true);

      // Initial or year-change load performs a complete refresh
      if (isLoadMore) {
        await fetchSummaries(true);
      } else {
        await Promise.all([fetchGlobalStats(), fetchSummaries(false)]);
      }
    } catch (err) {
      toast.error('获取赠送数据失败');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loading && !loadingMore && summaries.length < totalCount) {
      loadData(false, true);
    }
  };

  const openStudentLedger = async (student) => {
    try {
      setSelectedStudentForLedger(student);
      setStudentLedgerOpen(true);
      setLoadingStudentLedger(true);
      
      const res = await rewardService.getLedger({ 
        student_id: student.student_id,
        per_page: 200 // Get all records for specific student
      });
      
      setStudentLedgerItems(res.items || []);
    } catch (err) {
      toast.error('获取学员流水失败');
    } finally {
      setLoadingStudentLedger(false);
    }
  };

  const toggleExpandRow = (id) => {
    setExpandedLedgerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderDetailRow = (item) => {
    if (!expandedLedgerIds.has(item.id)) return null;
    if (!item.related_info) return null;

    const { related_info, action_type } = item;
    
    let content = null;
    if (['EARN_REFERRAL', 'EARN_VOLUME_BONUS'].includes(action_type)) {
      content = (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>{related_info.role_label || '学员'}：<strong>{related_info.student_name}</strong></span>
          <span>课程：<strong>{related_info.course_name}</strong></span>
          <span>购课日期：{related_info.date}</span>
          <span>购买课时：{formatHours(related_info.total_hours)}节</span>
        </div>
      );
    } else if (action_type === 'CONSUME') {
      content = (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>涉及课程：<strong>{related_info.course_name}</strong></span>
          <span>上课日期：{related_info.date}</span>
          <span>消耗量：{formatHours(Math.abs(related_info.consumed_hours))}节</span>
        </div>
      );
    } else if (action_type === 'VOID_CONSUME') {
      content = (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>原上课课程：<strong>{related_info.course_name}</strong></span>
          <span>上课日期：{related_info.date}</span>
        </div>
      );
    }

    if (!content) return null;

    return (
      <tr key={`${item.id}-detail`} className="bg-gray-50/50">
        <td colSpan="4" className="px-4 py-2 border-l-2 border-blue-400">
          {content}
        </td>
      </tr>
    );
  };

  const isFirstLoad = React.useRef(true);
  const prevYearRef = React.useRef(year);

  useEffect(() => {
    const isYearChanged = prevYearRef.current !== year;
    prevYearRef.current = year;

    if (isFirstLoad.current || isYearChanged) {
      isFirstLoad.current = false;
      loadData(isYearChanged, false);
    } else {
      // Only student filter or trigger changed -> localized refresh
      const updateTableOnly = async () => {
        setRefreshing(true);
        await fetchSummaries(false);
        setRefreshing(false);
      };
      updateTableOnly();
    }
  }, [year, filterStudentId, searchTrigger]);

  return (
    <div className="space-y-4 pb-24">
      {/* Header & Sub-Tabs */}
      <div className="flex items-center justify-between gap-4 py-1 border-b border-gray-100 mb-2">
        <div className="flex items-center gap-4">
          <div className="flex bg-gray-100 p-1 rounded-xl">
             <button
               onClick={() => setActiveSubTab('overview')}
               className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-all ${activeSubTab === 'overview' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
             >
               全景看板
             </button>
             <button
               onClick={() => setActiveSubTab('details')}
               className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-all ${activeSubTab === 'details' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
             >
               权益明细
             </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
           <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm">
             <Calendar size={14} className="text-gray-400" />
             <select 
               className="text-sm font-bold text-gray-700 focus:outline-none bg-transparent cursor-pointer"
               value={year}
               onChange={(e) => { setYear(e.target.value); }}
             >
               <option value="all">全部年份</option>
               {Array.from({ length: Math.max(1, new Date().getFullYear() - 2026 + 1) }).map((_, i) => {
                 const y = 2026 + i;
                 return <option key={y} value={y}>{y}年度数据</option>;
               })}
             </select>
           </div>
        </div>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-20 bg-white/50 rounded-2xl border border-gray-100 border-dashed">
            <div className="flex flex-col items-center gap-3">
              <RefreshCw className="animate-spin text-blue-500" size={32} />
              <span className="text-sm font-medium text-gray-400 italic">正在构建赠送全景视图...</span>
            </div>
          </div>
        ) : (
          <>
            {activeSubTab === 'overview' && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  {/* 左侧：激励撬动 ROI 看板 */}
                    <Card className="lg:col-span-3 p-3 shadow-sm border-[#EDF2F7] flex flex-col min-w-0">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-bold text-sm text-[#2D3748] flex items-center gap-2">
                          <TrendingUp className="text-blue-500" size={14} />
                          激励 ROI
                        </h3>
                      </div>

                <div className="flex-1 flex flex-col justify-center gap-2 min-w-0">
                  <div className="h-[140px] w-full">
                    {mounted && (
                      <ResponsiveContainer width="100%" height={140}>
                      <FunnelChart>
                        <Tooltip 
                          formatter={(value) => [`${value} 节`, '']}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '9px' }}
                        />
                        <Funnel
                          data={[
                            { value: stats.total_driven_hours || 0, name: '带动购课', fill: '#fbbf24' },
                            { value: (stats.total_issued || 0) + (stats.total_in_transit || 0), name: '预期赠送', fill: '#f43f5e' }
                          ]}
                          dataKey="value"
                        >
                          <LabelList position="right" fill="#4A5568" stroke="none" dataKey="name" fontSize={9} />
                        </Funnel>
                      </FunnelChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  
                  <div className="mt-0.5 p-2 bg-rose-50 rounded-lg border border-rose-100 shadow-sm transition-all hover:shadow-md">
                    <div className="flex items-center justify-between">
                       <span className="text-[11px] font-bold text-rose-700">激励杠杆 (ROI)</span>
                       <span className="text-base font-black text-rose-600">
                         {Number(stats.total_driven_hours || 0) > 0 ? (Number(stats.total_driven_hours) / (Number(stats.total_issued || 0) + Number(stats.total_in_transit || 0))).toFixed(1) : 0} : 1
                       </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* 右侧：赠送资产全景看板 */}
              <Card className="lg:col-span-2 p-3 shadow-sm border-[#EDF2F7] flex flex-col min-w-0">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-sm text-[#2D3748] flex items-center gap-2">
                    <PieChartIcon className="text-indigo-500" size={14} />
                    资产分布
                  </h3>
                </div>

                <div className="flex-1 flex flex-col min-w-0">
                   <div className="h-[120px] w-full relative mb-2">
                     {mounted && (
                       <ResponsiveContainer width="100%" height={120}>
                        <PieChart>
                          <Pie
                             data={[
                               { name: '已消', value: stats.total_consumed || 0, color: '#f97316' },
                               { name: '可用', value: stats.total_available || 0, color: '#3b82f6' },
                               { name: '在途', value: stats.total_in_transit || 0, color: '#818cf8' },
                             ]}
                             innerRadius={40}
                             outerRadius={55}
                             paddingAngle={5}
                             dataKey="value"
                          >
                            {[
                               { name: '已消', value: stats.total_consumed || 0, color: '#f97316' },
                               { name: '可用', value: stats.total_available || 0, color: '#3b82f6' },
                               { name: '在途', value: stats.total_in_transit || 0, color: '#818cf8' },
                            ].map((entry, index) => (
                               <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                             formatter={(value) => [`${value}节`, '']}
                             contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '9px' }}
                          />
                       </PieChart>
                     </ResponsiveContainer>
                   )}
                   <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                        <div className="text-[10px] text-gray-400 font-medium">总赠送</div>
                        <div className="text-sm font-black text-gray-700">
                          {formatHours(Number(stats.total_issued || 0) + Number(stats.total_in_transit || 0))}
                        </div>
                      </div>
                   </div>

                   <div className="flex flex-wrap items-center justify-between gap-1 mt-auto">
                      <div className="flex flex-col items-center flex-1 min-w-0 p-1 rounded-lg bg-gray-50/50">
                        <span className="text-[10px] text-gray-400">在途</span>
                        <span className="text-[11px] font-black text-gray-700 truncate">+{formatHours(stats.total_in_transit)}</span>
                      </div>
                      
                      <div className="flex flex-col items-center flex-1 min-w-0 p-1 rounded-lg bg-blue-50/50 border border-blue-50">
                        <span className="text-[10px] text-blue-500">可用</span>
                        <span className="text-[11px] font-black text-blue-600 truncate">+{formatHours(stats.total_available)}</span>
                      </div>

                      <div className="flex flex-col items-center flex-1 min-w-0 p-1 rounded-lg bg-orange-50/50 border border-orange-50">
                        <span className="text-[10px] text-orange-500">已消</span>
                        <span className="text-[11px] font-black text-orange-600 truncate">-{formatHours(stats.total_consumed)}</span>
                      </div>
                   </div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-6">

              <Card className="p-3 shadow-sm border-[#EDF2F7]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="text-yellow-500" size={16} />
                    <h3 className="font-bold text-sm text-[#2D3748]">价值贡献</h3>
                  </div>
                  <div className="text-[10px] text-gray-400 font-normal truncate">
                    气泡: 规模 | X: 撬动 | Y: 获得
                  </div>
                </div>

                <div className="mt-2 flex flex-col lg:flex-row gap-4 min-w-0">
                  {/* 下面散点图 */}
                  <div className="lg:w-3/5 h-[180px] min-w-0">
                    {(!stats.top_referrers || stats.top_referrers.length === 0) ? (
                      <div className="h-full flex flex-col items-center justify-center text-gray-400 text-[11px] gap-2">
                        <Target size={24} className="opacity-20" />
                        暂无维度数据
                      </div>
                    ) : mounted && (
                        <ResponsiveContainer width="100%" height={180}>
                        <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: -25 }}>
                          <XAxis 
                            type="number" 
                            dataKey="driven_hours" 
                            name="带动" 
                            unit="节" 
                            tick={{fontSize: 7}}
                          />
                          <YAxis 
                            type="number" 
                            dataKey="amount" 
                            name="赠送" 
                            unit="节" 
                            tick={{fontSize: 7}}
                          />
                          <ZAxis 
                            type="number" 
                            dataKey="referral_count" 
                            range={[20, 150]} 
                            name="推荐"
                          />
                          <Tooltip 
                            cursor={{ strokeDasharray: '3 3' }} 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-2 border border-blue-100 shadow-xl rounded-lg text-[10px]">
                                    <p className="font-bold text-gray-800 mb-1 border-b border-gray-50 pb-1">{data.name}</p>
                                    <div className="space-y-0.5 text-gray-600">
                                      <p>推荐：<span className="text-blue-500 font-black">{data.referral_count}人</span></p>
                                      <p>带动：<span className="text-amber-500 font-black">{formatHours(data.driven_hours)}节</span></p>
                                      <p>获得：<span className="text-rose-500 font-black">{formatHours(data.amount)}节</span></p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Scatter name="推荐贡献" data={stats.top_referrers}>
                            {stats.top_referrers.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={['#4299E1', '#48BB78', '#ED8936', '#9F7AEA', '#F56565'][index % 5]} 
                                fillOpacity={0.8}
                                strokeWidth={1}
                                stroke={['#3182CE', '#38A169', '#DD6B20', '#805AD5', '#D53F8C'][index % 5]}
                              />
                            ))}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* 右侧：TOP 榜单 */}
                  <div className="flex-[2] bg-gray-50/50 rounded-lg p-2 border border-gray-100 border-dashed">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-1 text-[10px] font-black text-gray-500 uppercase tracking-tight">
                         <Target size={10} className="text-indigo-500" />
                         TOP 10 推荐榜
                       </div>
                     </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {(stats.top_referrers || [])
                          .slice()
                          .sort((a, b) => (b.referral_count || 0) - (a.referral_count || 0))
                          .slice(0, 10)
                          .map((r, idx) => (
                          <div key={idx} className="flex items-center gap-2 group">
                             <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-black ${
                                idx === 0 ? 'bg-amber-100 text-amber-600' : 
                                idx === 1 ? 'bg-gray-200 text-gray-600' :
                                idx === 2 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'
                             }`}>
                               {idx + 1}
                             </div>
                             <div className="flex-1 min-w-0">
                               <div className="text-[11px] font-bold text-gray-700 truncate group-hover:text-blue-600 transition-colors">
                                 {r.name}
                               </div>
                               <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[9px] text-gray-400 font-medium">{r.referral_count}人</span>
                                  <div className="w-px h-1.5 bg-gray-200"></div>
                                  <span className="text-[9px] font-black text-blue-500">{formatHours(r.amount)}节</span>
                               </div>
                             </div>
                          </div>
                        ))}
                        {(!stats.top_referrers || stats.top_referrers.length === 0) && (
                          <div className="col-span-2 text-center py-6 text-[11px] text-gray-400 italic">
                            暂无活跃推荐
                          </div>
                        )}
                     </div>
                  </div>
                  </div>
                </Card>
              </div>
            </>
          )}

            {activeSubTab === 'details' && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-3">

            <Card className="overflow-hidden border-[#EDF2F7]">
              <div className="px-3 py-2 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-1.5">
                   <Trophy size={14} className="text-blue-500"/>
                   <h3 className="font-bold text-xs text-[#2D3748]">资产明细</h3>
                </div>
                <div className="flex items-center">
                  <select 
                    className="border border-[#EDF2F7] rounded-md px-2 py-1 text-[11px] bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 w-32"
                    value={filterStudentId}
                    onChange={(e) => {
                      setFilterStudentId(e.target.value);
                    }}
                  >
                    <option value="">快速筛选...</option>
                    {Array.isArray(activeStudents) && activeStudents.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto relative min-h-[100px]">
                {refreshing && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
                    <RefreshCw className="animate-spin text-blue-500" size={16} />
                  </div>
                )}
                <table className={`w-full text-xs text-left table-fixed transition-opacity duration-200 ${refreshing ? 'opacity-70 pointer-events-none' : ''}`}>
                  <thead className="bg-[#F7FAFC] border-b border-gray-100 text-[#718096]">
                    <tr>
                      <th className="px-3 py-2 font-black w-24">学生</th>
                      <th className="px-1 py-2 font-black text-center">在途</th>
                      <th className="px-1 py-2 font-black text-center">可用</th>
                      <th className="px-1 py-2 font-black text-center">已消</th>
                      <th className="px-3 py-2 font-black text-center w-16 text-[#2D3748]">赠送</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summaries.map((s, idx) => (
                      <tr key={s.student_id} className={`hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}>
                        <td className="px-3 py-2 truncate">
                          <button 
                            onClick={() => openStudentLedger(s)}
                            className="text-blue-600 font-bold hover:underline truncate"
                            title={s.student_name}
                          >
                            {s.student_name}
                          </button>
                        </td>
                        <td className="px-1 py-2 text-center text-indigo-500 font-black">{Number(s.in_transit || 0) > 0 ? `+${formatHours(s.in_transit)}` : '0'}</td>
                        <td className="px-1 py-2 text-center text-blue-600 font-black">+{formatHours(s.available_reward)}</td>
                        <td className="px-1 py-2 text-center text-gray-400 italic">-{formatHours(s.consumed_reward)}</td>
                        <td className="px-3 py-2 text-center font-black text-[#2D3748]">{formatHours(s.total_reward)}</td>
                      </tr>
                    ))}
                    {summaries.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-400 text-[10px]">
                          暂无资产匹配结果
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Footer */}
              <LoadMoreButton 
                onClick={handleLoadMore} 
                loading={loadingMore} 
                hasMore={summaries.length > 0 && summaries.length < totalCount} 
              />
            </Card>

            {/* 计算口径说明 */}
            <Card className="p-3 bg-gray-50 border-gray-100 rounded-lg">
              <h4 className="text-[13px] font-black text-[#2D3748] mb-1.5 flex items-center gap-1.5 uppercase tracking-wider">
                <Info size={14} className="text-blue-500" />
                资产规则
              </h4>
              <ul className="text-[11px] text-gray-400 space-y-1 leading-relaxed">
                <li className="flex gap-1.5"><span className="text-gray-300">•</span> <span><strong>总赠送</strong>：历史累计，即 在途+可用+已消。</span></li>
                <li className="flex gap-1.5"><span className="text-gray-300">•</span> <span><strong>在途</strong>：购课后预期入账，满足条件（如签到3节或结课）后转为可用。</span></li>
                <li className="flex gap-1.5"><span className="text-gray-300">•</span> <span><strong>已消</strong>：净消课课时 (实际核销减去作废退回)。</span></li>
              </ul>
            </Card>
          </div>
        )}
      </>
    )}
  </div>

      {/* 侧边 Drawer */}
      {drawerOpen && selectedRecord && (
        <div className="absolute inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setDrawerOpen(false)}>
          <div 
            className="w-full max-w-sm bg-white h-full shadow-2xl flex flex-col transform transition-transform" 
            onClick={e => e.stopPropagation()}
          >
            <div className="h-[56px] px-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#2D3748]">流水明细</h2>
              <button onClick={() => setDrawerOpen(false)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">动作类型 / 状态</div>
                  <div className="font-medium flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${ACTION_BADGES[selectedRecord.action_type]?.color}`}>
                      {ACTION_BADGES[selectedRecord.action_type]?.label || selectedRecord.action_type}
                    </span>
                    {selectedRecord.status && (
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${STATUS_BADGES[selectedRecord.status]?.color}`}>
                        {STATUS_BADGES[selectedRecord.status]?.label || selectedRecord.status}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">学员/推荐人</div>
                  <div className="font-medium">{selectedRecord.student_name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">触发时间</div>
                  <div>{selectedRecord.created_at}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">变动额度</div>
                  <div className={`font-bold ${
                    selectedRecord.status === 'CANCELLED'
                      ? 'text-gray-400 line-through opacity-70'
                      : (Number(selectedRecord.amount || 0) > 0 ? 'text-green-600' : 'text-gray-600')
                  }`}>
                    {Number(selectedRecord.amount || 0) > 0 ? '+' : ''}{formatHours(selectedRecord.amount)} 节
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-2">深度追踪 (Metadata)</div>
                  <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto border border-gray-200 text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(selectedRecord.metadata || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 学员流水明细 Modal */}
      {studentLedgerOpen && (
        <div className="absolute inset-0 bg-black/30 z-[60] flex items-center justify-center">
          <div className="bg-white w-full h-full flex flex-col shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom duration-300">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-bold text-lg text-[#2D3748] flex items-center gap-2">
                  <Trophy size={20} className="text-yellow-500" />
                  {selectedStudentForLedger?.student_name}的赠送流水
                </h3>
              </div>
              <button onClick={() => setStudentLedgerOpen(false)} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {loadingStudentLedger ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <RefreshCw className="animate-spin text-blue-500" size={32} />
                  <span className="text-sm text-gray-500">正在拼命加载流水数据...</span>
                </div>
              ) : studentLedgerItems.length === 0 ? (
                <div className="text-center py-20 text-gray-500">暂无流水记录</div>
              ) : (
                <div className="overflow-hidden border border-gray-100 rounded-xl">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-white border-b border-gray-100 text-gray-500">
                      <tr>
                        <th className="px-4 py-3 font-medium">触发日期</th>
                        <th className="px-4 py-3 font-medium">动作</th>
                        <th className="px-4 py-3 font-medium text-right">课时</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {studentLedgerItems.map((item) => {
                        const badge = ACTION_BADGES[item.action_type] || { color: 'bg-gray-100', label: item.action_type };
                        const canExpand = ['EARN_REFERRAL', 'EARN_VOLUME_BONUS', 'CONSUME', 'VOID_CONSUME'].includes(item.action_type);

                        return (
                          <React.Fragment key={item.id}>
                            <tr className="hover:bg-blue-50/30 transition-colors">
                              <td className="px-4 py-3 text-gray-600">{item.created_at?.replace('T', ' ')}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col gap-1 items-start">
                                  <span className={`px-2 py-1 rounded-md text-xs font-medium ${badge.color}`}>
                                    {badge.label}
                                  </span>
                                  {item.status && (
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_BADGES[item.status]?.color}`}>
                                      {STATUS_BADGES[item.status]?.label}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {canExpand ? (
                                  <button 
                                    onClick={() => toggleExpandRow(item.id)}
                                    className={`font-bold px-2 py-0.5 rounded transition-all flex items-center gap-1 ml-auto ${
                                      item.status === 'CANCELLED' 
                                        ? 'text-gray-400 line-through opacity-70' 
                                        : (item.amount > 0 ? 'text-green-600 hover:bg-green-50' : 'text-gray-600 hover:bg-gray-100')
                                    } ${expandedLedgerIds.has(item.id) ? 'ring-1 ring-blue-400' : ''}`}
                                  >
                                    {item.amount > 0 ? '+' : ''}{formatHours(item.amount)}
                                    {expandedLedgerIds.has(item.id) ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                                  </button>
                                ) : (
                                  <span className={`font-bold ${
                                    item.status === 'CANCELLED' 
                                      ? 'text-gray-400 line-through opacity-70' 
                                      : (item.amount > 0 ? 'text-green-600' : 'text-gray-600')
                                  }`}>
                                    {item.amount > 0 ? '+' : ''}{formatHours(item.amount)}
                                  </span>
                                )}
                              </td>
                            </tr>
                            {renderDetailRow(item)}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
              <Button onClick={() => setStudentLedgerOpen(false)}>关闭退出</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardManagementView;
